import React from 'react';
import type {ReactNode} from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Spacer, spacing} from '@nfq/react-grid';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {stepAnimation} from '../../animations/stepAnimation';
import {P} from '../../layout/Text';

import {useTabs} from './useTabs';

import type {Hash, TabComponent} from '../../../utils/childTypes';

/**
 * Represents the structure for each tab header item in the `Tabs` component.
 * Each tab is identified by a unique hash value and displays a label, which can be a string or any ReactNode.
 * This is used to define the clickable tab headers and their corresponding content.
 */
export interface TabHeadItems {
    /**
     * A unique string that identifies the tab. This value is used to match the tab with its corresponding content.
     */
    hash: Hash;
    /**
     * The label to be displayed on the tab. This can be a string or any ReactNode, allowing for flexible tab header content.
     */
    label: ReactNode;
}

/**
 * Interface representing the props for the `Tabs` component. This component manages the display of tabbed content,
 * allowing users to switch between different sections by clicking on corresponding tab headers.
 */
export interface ComponentProps {
    /**
     * Represents the currently active tab. This is a unique identifier, usually a hash string,
     * that corresponds to the tab content being displayed.
     */
    activeTab: Hash;
    /**
     * An array of `TabComponent` elements that represent the content associated with each tab.
     * Each child is matched with a tab based on its corresponding hash.
     */
    children: TabComponent[];
    /**
     * An array of `TabHeadItems`, representing the header items for each tab. These items contain a hash and label,
     * which help users navigate between the different tabbed sections.
     */
    headItems: TabHeadItems[];
    /**
     * A boolean flag that indicates whether all tabs should be pre-mounted. When set to `true`, all tab content will be rendered in the DOM, even if it is not currently active. This can be useful for performance optimization in certain scenarios, but may increase initial load time.
     *
     * @default false
     */
    isPremountingAllTabs?: boolean;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Tabs'
     */
    testId?: string;
}

/**
 * Renders a `Tabs` component that allows navigation between different sections or views.
 * The component takes a list of head items representing each tab and displays the corresponding child content based on the active tab.
 * Transitions between tabs are animated using Framer Motion, providing a smooth user experience.
 * The `useTabs` hook is used to manage the current tab state and direction of tab transitions.
 *
 * @param props                      The component props.
 * @param props.activeTab            The initially active tab, identified by its hash value.
 * @param props.children             The content of the tabs, where each child corresponds to a tab section.
 * @param props.headItems            An array of tab headers, each with a label and hash value.
 * @param props.isPremountingAllTabs A flag indicating whether all tabs should be pre-mounted in the DOM, even if they are not currently active.
 * @param props.testId               A unique identifier for testing purposes.
 * @returns A React component that renders a tabbed interface with animated transitions.
 *
 * @example
 * ```tsx
 * const headItems = [
 *   { hash: 'tab1', label: 'Tab 1' },
 *   { hash: 'tab2', label: 'Tab 2' }
 * ];
 *
 * <Tabs activeTab="tab1" headItems={headItems}>
 *   <TabContent1 type={{ hash: 'tab1' }} />
 *   <TabContent2 type={{ hash: 'tab2' }} />
 * </Tabs>
 * ```
 */
const Tabs = ({activeTab, children, headItems, isPremountingAllTabs = false, testId = 'Tabs'}: ComponentProps) => {
    const {activeChild, colors, direction, handleTab, inactiveChilds, tab} = useTabs(
        activeTab,
        children,
        isPremountingAllTabs
    );

    return (
        <Wrapper data-cy={testId}>
            <Head>
                {headItems.map(item => (
                    <Tab key={item.hash} onClick={() => handleTab(item.hash)}>
                        <TabText>
                            <Color $color={item.hash === tab ? colors.primary : colors.textSecondary}>
                                {item.label}
                            </Color>
                        </TabText>
                        {item.hash === tab && <Indicator layoutId="Indicator" layout />}
                    </Tab>
                ))}
            </Head>
            <Spacer y={4} isNotStretching />
            <Body>
                <TabWrapper>
                    <AnimatePresence custom={direction} initial={false} mode="sync">
                        <AnimationWrapper
                            key={tab}
                            animate="enter"
                            custom={direction}
                            exit="exit"
                            initial="initial"
                            variants={stepAnimation}
                        >
                            {activeChild}
                        </AnimationWrapper>
                    </AnimatePresence>
                </TabWrapper>
            </Body>
            {isPremountingAllTabs && <PreMountCache aria-hidden="true">{inactiveChilds}</PreMountCache>}
        </Wrapper>
    );
};

Tabs.displayName = 'Tabs';

export {Tabs};

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    width: 100%;
`;

const Head = styled.div`
    border-bottom: 1px solid ${({theme}) => theme.colors.neutral20};
    display: flex;
    flex: 0 0 fit-content;
    width: 100%;
`;

const Tab = styled.div`
    cursor: pointer;
    display: flex;
    flex: 1 1 auto;
    justify-content: center;
    padding: ${spacing(3)} 0;
    position: relative;
`;

const TabText = styled(P)`
    align-items: center;
    display: flex;
    font-weight: bold;
    gap: ${spacing(1)};
    justify-content: center;
`;

const Indicator = styled(motion.div)`
    background-color: ${({theme}) => theme.colors.primary};
    bottom: -1px;
    height: 0.4rem;
    position: absolute;
    width: 100%;
`;

const Body = styled.div`
    display: flex;
    flex: 1 1 auto;
    overflow: hidden;
    width: 100%;
`;

const TabWrapper = styled(motion.div)`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    height: 100%;
    width: 100%;
`;

const AnimationWrapper = styled(motion.div)`
    display: grid;
    grid-column-start: 1;
    grid-row-start: 1;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
    height: auto;
    max-height: 100%;
    min-height: 100%;
`;

const PreMountCache = styled.div`
    height: 0;
    left: -10000px;
    overflow: hidden;
    pointer-events: none;
    position: fixed;
    top: 0;
    visibility: hidden;
    width: 0;
`;